<template>
	<div
		class="carousel"
		:class="`bg-${bg}`"
		:style="`height: ${height}; width: ${width};`"
	>
		<div
			:class="`swiper-container`"
			:id="`${identifier}`"
			:key="`${identifier}`"
			ref="swiperContainer"
		>
			<div :class="`swiper-wrapper`">
				<slot />
			</div>
			<!-- Add Pagination -->
			<div
				:class="`swiper-pagination swiper-pagination-${identifier}`"
				v-if="dots || (scrollbar && loop)"
				:key="`${identifier}`"
			/>
			<div
				:class="`swiper-scrollbar swiper-scrollbar-${identifier}`"
				v-if="scrollbar && !loop"
				:key="`${identifier}`"
			/>
		</div>
		<div
			:class="`swiper-button-prev swiper-button-prev-${identifier}`"
			v-if="navigation"
			tabindex="0"
			@keydown.enter="prevSlide"
		/>
		<div
			:class="`swiper-button-next swiper-button-next-${identifier}`"
			v-if="navigation"
			tabindex="0"
			@keydown.enter="nextSlide"
		/>
	</div>
</template>
<script setup>
import Swiper from "swiper";
import { Navigation, Pagination, Scrollbar } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/scrollbar";
defineComponent({
	name: "SwiperCarousel",
});
const props = defineProps({
	height: {
		type: String,
		default: "100%",
	},
	width: {
		type: String,
		default: "100%",
	},
	center: {
		type: Boolean,
		default: false,
	},
	loop: {
		type: Boolean,
		default: false,
	},
	slidesPerView: {
		type: Number,
		default: 1,
	},
	dots: {
		type: Boolean,
		default: false,
	},
	scrollbar: {
		type: Boolean,
		default: false,
	},
	spaceBetween: {
		type: Number,
		default: 0,
	},
	slideToClickedSlide: {
		type: Boolean,
		default: false,
	},
	navigation: {
		type: Boolean,
		default: true,
	},
	bg: {
		type: String,
		default: "transparent",
	},
});

const identifier = `A${useId()}`;
const swiperContainer = ref(null);
const prevSlide = () => {
	swiperContainer.value.swiper.slidePrev();
};
const nextSlide = () => {
	swiperContainer.value.swiper.slideNext();
};
const initializeSwiper = (id) => {
	new Swiper(`#${id}`, {
		// Swiper options
		/*
      Space between set to +50 because of padding in swiper-container
      Padding is set to 50px to get navigation buttons outside of the carousel elements
    */
		spaceBetween: props.spaceBetween + 50,
		centeredSlides: props.center,
		loop: props.loop,
		slidesPerView: props.slidesPerView,
		touchEventsTarget: "container",
		pagination: {
			el: `.swiper-pagination-${id}`,
			clickable: true,
		},
		navigation: {
			nextEl: `.swiper-button-next-${id}`,
			prevEl: `.swiper-button-prev-${id}`,
		},
		scrollbar: {
			el: `.swiper-scrollbar-${id}`,
			// Makes the Scrollbar Draggable
			draggable: true,
			// Snaps slider position to slides when you release Scrollbar
			snapOnRelease: true,
			// Size (Length) of Scrollbar Draggable Element in px
			dragSize: "auto",
		},
		modules: [Navigation, Pagination, Scrollbar],
	});
};
onMounted(async () => {
	await nextTick();
	const id = swiperContainer.value.id;
	/*
    Get ID from ref to initialize Swiper – this is used for initializing more than one swiper instance on the same page
  */
	initializeSwiper(id);
});
watch(
	() => props.slidesPerView,
	(newVal) => {
		if (swiperContainer.value && swiperContainer.value.swiper) {
			swiperContainer.value.swiper.params.slidesPerView = newVal;
			swiperContainer.value.swiper.update();
		}
	},
);
</script>
<style lang="scss" scoped>
.bg {
	&-dark {
		background-color: #454545;
	}

	&-white {
		background-color: white;
	}

	&-transparent {
		background-color: transparent;
	}
}

* {
	user-select: none;
}

.carousel {
	max-width: 100vw;
	max-height: 100vh;
	position: relative;
}

.swiper-container {
	width: 100%;
	height: 100%;
	overflow: hidden;
	cursor: grab;
	padding: 0 40px;
}

.swiper-button-prev::after,
.swiper-button-next::after {
	font-size: 24px;
	color: black !important;
}

.swiper-button-next {
	right: 0;
}

.swiper-button-prev {
	left: 0;
}

.swiper-pagination {
	position: absolute;
	text-align: center;
	transition: 300ms opacity;
	transform: translate3d(0, 0, 0);
	z-index: 10;

	:deep() {
		.swiper-pagination-bullet {
			background-color: black !important;
			width: 10px;
			height: 10px;
			margin: 0 10px;
		}
	}
}

.swiper-scrollbar {
	height: 8px;
	cursor: pointer;

	:deep() {
		.swiper-scrollbar-drag {
			background-color: black;
		}
	}
}

.swiper-pagination-bullets {
	bottom: 4px !important;
	pointer-events: all !important;
}

.swiper-wrapper {
	width: 100%;
	height: 100%;
	align-items: center;
}
</style>
